import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Heading,
  InlineMessage,
  Link,
  TextField,
} from '@invisionag/components';
import { Trans, useTranslation } from '@invisionag/next/i18n';
import { Result } from '@invisionag/next/result';
import * as Cookie from 'cookie';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { CenteredContainer } from '../../_components/centered-container';
import { SetView } from './page';
import { getRedirectUrl } from '@/lib/utils';
import {
  Form,
  ValidationErrors,
  FormMessage,
  FormResult,
  SubmitButton,
} from '@/lib/form';
import * as Cortex from '@/repositories/cortex';
import { unreachable } from '@/lib/exhaustive';
import { Divider } from '@/app/(public)/_components/divider';

type SuccessVariants =
  'password-reset' | 'initial-password' | 'password-changed';

export function LoginForm({
  setView,
  message,
}: {
  setView: SetView;
  message: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('return_url');
  const [success, setSuccess] = useState<SuccessVariants | undefined>(() => {
    const successParam = searchParams.get('success');
    if (
      successParam === 'password-reset' ||
      successParam === 'initial-password' ||
      successParam === 'password-changed'
    ) {
      return successParam;
    }
    return undefined;
  });

  useEffect(() => {
    const filteredParams = new URLSearchParams(searchParams);
    if (filteredParams.has('success')) {
      filteredParams.delete('success');
      router.replace(`/login?${filteredParams.toString()}`);
    }
  }, [router, searchParams]);

  async function login(formData: FormData): Promise<FormResult> {
    setSuccess(undefined);

    const email = assertString(formData.get('email'));
    const password = assertString(formData.get('password'));

    if (!email || !password) {
      const validationErrors: ValidationErrors = {};

      if (!email) {
        validationErrors.email = t(
          'login.login-form.field.email.validation.valueMissing',
        );
      }

      if (!password) {
        validationErrors.password = t(
          'login.login-form.field.password.validation.valueMissing',
        );
      }

      return Result.Err({ validationErrors });
    }

    const result = await Cortex.login(email, password);

    if (!result.ok) {
      return Result.Err({ message: t(`login.login-form.error.${result.err}`) });
    }

    if (result.val) {
      const { locale, requiredAction } = result.val;

      updateLocale(locale, router);

      switch (requiredAction) {
        case 'login-otp-verification':
          setView('otp');
          return Result.Ok();
        case 'setup-two-factor-authentication': {
          const { secret, recoveryCodes, provisioningUri } =
            await Cortex.create2fa();

          if (!recoveryCodes) throw new Error('Missing recovery codes');

          setView('setupTwoFactorAuth', {
            secret,
            recoveryCodes,
            provisioningUri,
          });

          return Result.Ok();
        }
        default:
          unreachable('requiredAction', requiredAction);
      }
    }

    router.replace(getRedirectUrl(returnUrl));
    return Result.Ok();
  }

  return (
    <CenteredContainer>
      <Heading level={1} className="text-center">
        {t('login.login-form.heading')}
      </Heading>
      {success && (
        <InlineMessage variant="success">
          {t(`login.login-form.success.${success}`)}
        </InlineMessage>
      )}
      <Form className="grid gap-4" action={login}>
        <div className="grid gap-4">
          <TextField
            isRequired
            label={t('login.login-form.field.email.label')}
            name="email"
            type="email"
            autoFocus
          />
          <TextField
            isRequired
            label={t('login.login-form.field.password.label')}
            name="password"
            type="password"
          />
        </div>
        <FormMessage />
        {message && <InlineMessage variant="error">{message}</InlineMessage>}
        <SubmitButton className="mt-2 w-full">
          {t('login.login-form.submit')}
        </SubmitButton>
      </Form>
      <div className="justify-self-center text-center">
        <Trans
          i18n={i18n}
          i18nKey="login.login-form.request-password-reset-hint"
          components={{
            a: (
              <Link
                href={`/request-password-reset?${searchParams.toString()}`}
              />
            ),
          }}
        />
      </div>
      <Divider label={t('login.divider.label')} />
      <Link
        className="justify-self-center"
        href={`/login/sso?${searchParams.toString()}`}
      >
        {t('login.login-form.login-sso-link')}
      </Link>
    </CenteredContainer>
  );
}

function assertString(value: unknown): string {
  if (typeof value === 'string') return value;
  throw new Error(`Expected string, got '${typeof value}'`);
}

function updateLocale(locale: string, router: AppRouterInstance) {
  document.cookie = Cookie.stringifySetCookie({
    name: 'peopleware-locale',
    value: locale,
    maxAge: 5 * 60,
    secure: process.env.NODE_ENV !== 'development',
    sameSite: 'strict',
  });

  router.refresh();
}
